import {
  parseAsInteger,
  parseAsString,
  parseAsArrayOf,
  useQueryStates,
} from 'nuqs'
import {
  Container,
  HeadMetatags,
  InnerPageLayout,
} from 'routes/components/Layout'
import { IntegrationCategoriesProvider } from 'routes/Integrations/context/IntegrationCategoriesContext'
import { CategoryCompactType } from 'routes/Integrations/helpers/types'
import { useIntegrations } from 'routes/Integrations/hooks/useIntegrations'
import { useCategories } from './hooks/useCategories'
import { IntegrationsHero } from './components/Hero'
import { IntegrationsSearch } from './components/Search'
import { IntegrationsFilters } from './components/Filters'
import { IntegrationsCategoriesGrid } from './components/CategoriesGrid'
import { IntegrationsCategoriesSelect } from './components/CategoriesSelect'
import { IntegrationsSearchSummary } from './components/Summary'
import { IntegrationsGridSkeleton } from './components/Grid/IntegrationsGridSkeleton'
import { IntegrationsContent } from './components/Content'
import { IntegrationsCustomBlock } from './components/CustomBlock'
import classes from './Integrations.module.scss'

export default function IntegrationsPage({
  categories,
}: {
  categories: CategoryCompactType[]
}) {
  return (
    <InnerPageLayout>
      <HeadMetatags
        title={'Supported Integrations'}
        canonical={`/integrations`}
      ></HeadMetatags>
      <IntegrationCategoriesProvider categories={categories}>
        <IntegrationsPageWithQuery />
      </IntegrationCategoriesProvider>
    </InnerPageLayout>
  )
}

function IntegrationsPageWithQuery() {
  const { integrations, totalAmount, loadingIntegrations } = useIntegrations()

  const { filteredCategories, loadingCategories } = useCategories()

  const [queryState, setQueryState] = useQueryStates({
    search: parseAsString.withDefault(''),
    category: parseAsString.withDefault(''),
    filter: parseAsArrayOf(parseAsString, ',').withDefault([]),
    page: parseAsInteger.withDefault(1),
  })

  const { search, category, filter } = queryState

  const noFoundIntegrations = integrations.length === 0

  function handleSearchChange(search: string) {
    void setQueryState({
      search: search,
      category: null,
      filter: filter,
      page: null,
    })
  }

  function handleCategorySelect(category: string) {
    void setQueryState({
      search: null,
      category: category,
      filter: filter,
      page: null,
    })
  }

  function handleFilterSelect(filter: string[]) {
    void setQueryState({
      search: search,
      category: category,
      filter: filter,
      page: null,
    })
  }

  return (
    <Container className={classes.container}>
      <IntegrationsHero className={classes.hero} />
      <section className={classes.sidebar}>
        <IntegrationsSearch value={search} onChange={handleSearchChange} />
        <IntegrationsFilters value={filter} onChange={handleFilterSelect} />
        <IntegrationsCategoriesGrid
          filteredCategories={filteredCategories}
          loading={loadingCategories}
          value={category}
          onChange={handleCategorySelect}
        />
        <IntegrationsCategoriesSelect
          filteredCategories={filteredCategories}
          loading={loadingCategories}
          value={category}
          onChange={handleCategorySelect}
        />
      </section>
      <section className={classes.content}>
        <IntegrationsSearchSummary
          total={totalAmount}
          isLoading={loadingIntegrations}
          isEmptyList={noFoundIntegrations}
        />
        {loadingIntegrations ? (
          <IntegrationsGridSkeleton />
        ) : (
          <IntegrationsContent
            integrations={integrations}
            total={totalAmount}
          />
        )}
        <IntegrationsCustomBlock />
      </section>
    </Container>
  )
}
